<script lang="ts">
/**
 * Shared study deck: theme, particles, logo, progress, nav, badges, dev export, Chart.js preload,
 * and slide vs reader layout (via `layout`). Used by `StudyPresentation` (mode toggle) and thin
 * `StudySlidePresentation` / `StudyReaderPresentation` wrappers.
 */
import { browser, building } from '$app/environment'
import { page } from '$app/state'
import {
  STUDY_CHART_JS_GATE,
  type StudyChartJsGate,
} from '$lib/components/study/study-chart-js-gate'
import StudyPresentationLogoHeader from '$lib/components/study/StudyPresentationLogoHeader.svelte'
import { formatCount } from '$lib/utils/report/calculations'
import type { StudyMeta } from '$lib/utils/report/types'
import { onDestroy, onMount, setContext, tick } from 'svelte'
import type { Snippet } from 'svelte'

interface Props {
  /** Browser tab title. */
  title: string
  /** All slide content. Receives `goTo`, `activeSlideId`, and `slideActive` for chart lazy-build. */
  children: Snippet<
    [
      {
        goTo: (idx: number) => void
        activeSlideId: string
        /** In reader layout, always true so all slides preload charts/maps. */
        slideActive: (slideId: string) => boolean
      },
    ]
  >
  /** One slide at a time vs stacked reader layout. */
  layout: 'slide' | 'reader'
  /**
   * When set (e.g. from `StudyPresentation`), HTML export switches to reader first so all slides
   * can render; then `restoreSlideModeAfterExport` runs in `finally`.
   */
  requestReaderModeForExport?: () => Promise<void>
  restoreSlideModeAfterExport?: () => Promise<void>
  /** Optional study metadata — when provided, renders a citation chip in the bottom-right corner. */
  meta?: StudyMeta
}

let {
  title,
  children,
  layout,
  requestReaderModeForExport,
  restoreSlideModeAfterExport,
  meta,
}: Props = $props()

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  })
}

const chartJsGate = $state<StudyChartJsGate>({ ready: false })
setContext(STUDY_CHART_JS_GATE, chartJsGate)

/** Light palette when `?light_theme=1` is present (studies only). */
const lightTheme = $derived(!building && page.url.searchParams.get('light_theme') === '1')

/** HTML export control — only when `?mode=dev` is present. */
const devMode = $derived(!building && page.url.searchParams.get('mode') === 'dev')

$effect(() => {
  if (!browser) return
  const root = document.documentElement
  if (lightTheme) {
    root.classList.remove('dark')
    root.style.colorScheme = 'light'
  } else {
    root.classList.add('dark')
    root.style.colorScheme = 'dark'
  }
})

// ── Active slide tracking (drives chart component builds) ─────────────────
let activeSlideId = $state('')
let slideIdCopied = $state(false)
let copyFeedbackTimer: ReturnType<typeof setTimeout> | undefined

let stageEl = $state<HTMLDivElement | undefined>(undefined)
let slideIntersectionObserver: IntersectionObserver | undefined

onDestroy(() => {
  slideIntersectionObserver?.disconnect()
  slideIntersectionObserver = undefined
  if (copyFeedbackTimer !== undefined) clearTimeout(copyFeedbackTimer)
  if (!browser) return
  const root = document.documentElement
  root.classList.add('dark')
  root.style.colorScheme = ''
  root.style.backgroundColor = ''
  root.style.minHeight = ''
  document.body.style.backgroundColor = ''
  document.body.style.minHeight = ''
})

// ── Navigation: slide deck vs reader ───────────────────────────────────────
let currentSlide = $state(0)
let totalSlides = $state(0)

/** Reactive so layout toggles cannot reset the bottom nav / top progress to template defaults. */
const navCounterLabel = $derived(totalSlides > 0 ? `${currentSlide + 1} / ${totalSlides}` : '1 / …')
const navProgressPct = $derived(totalSlides > 0 ? ((currentSlide + 1) / totalSlides) * 100 : 0)

function syncNavDots(): void {
  if (!browser || totalSlides <= 0) return
  document
    .querySelectorAll('.nd')
    .forEach((dot, i) => dot.classList.toggle('active', i === currentSlide))
}

function syncNavButtons(): void {
  if (!browser) return
  const nav = document.getElementById('nav-bar') as HTMLElement | null
  const bp = document.getElementById('bp') as HTMLButtonElement | null
  const bn = document.getElementById('bn') as HTMLButtonElement | null
  if (nav) {
    nav.style.removeProperty('opacity')
    nav.style.removeProperty('pointer-events')
  }
  if (bp) {
    bp.onclick = prev
    bp.classList.toggle('nb--disabled', totalSlides <= 0 || currentSlide <= 0)
  }
  if (bn) {
    bn.onclick = next
    bn.classList.toggle('nb--disabled', totalSlides <= 1 || currentSlide >= totalSlides - 1)
  }
}

function applyActiveSlideIndex(idx: number): void {
  if (layout !== 'reader') return
  const wraps = document.querySelectorAll<HTMLElement>('.stage .study-reader-slide-wrap')
  if (idx < 0 || idx >= wraps.length) return
  currentSlide = idx
  wraps.forEach((wrap, i) => {
    const slide = wrap.querySelector<HTMLElement>('.slide')
    if (slide) slide.classList.toggle('active', i === idx)
  })
  const slide = wraps[idx].querySelector<HTMLElement>('.slide')
  if (slide) {
    activeSlideId = slide.id
    slideIdCopied = false
    if (copyFeedbackTimer !== undefined) {
      clearTimeout(copyFeedbackTimer)
      copyFeedbackTimer = undefined
    }
  }
  syncNavDots()
  syncNavButtons()
}

function wrapReaderSlides(): void {
  const stage = stageEl
  if (!stage) return
  const directSlides = [...stage.querySelectorAll<HTMLElement>(':scope > .slide')]
  for (const slide of directSlides) {
    const wrap = document.createElement('div')
    wrap.className = 'study-reader-slide-wrap'
    slide.parentNode?.insertBefore(wrap, slide)
    wrap.appendChild(slide)
  }
}

function unwrapReaderSlides(): void {
  const stage = stageEl
  if (!stage) return
  const wraps = [...stage.querySelectorAll<HTMLElement>('.study-reader-slide-wrap')]
  for (const wrap of wraps) {
    const slide = wrap.querySelector<HTMLElement>('.slide')
    if (slide) stage.insertBefore(slide, wrap)
    wrap.remove()
  }
}

async function transitionLayoutFromTo(
  from: 'slide' | 'reader',
  to: 'slide' | 'reader',
): Promise<void> {
  if (from === to) return
  if (from === 'slide' && to === 'reader') {
    slideIntersectionObserver?.disconnect()
    slideIntersectionObserver = undefined
    const dotsContainer = document.getElementById('dots')
    if (dotsContainer) dotsContainer.innerHTML = ''
    await tick()
    wrapReaderSlides()
    const slides = document.querySelectorAll<HTMLElement>('.stage .study-reader-slide-wrap .slide')
    totalSlides = slides.length
    const readerIdx = totalSlides > 0 ? Math.min(Math.max(0, currentSlide), totalSlides - 1) : 0
    if (dotsContainer) {
      slides.forEach((_, i) => {
        const dot = document.createElement('div')
        dot.className = 'nd' + (i === readerIdx ? ' active' : '')
        dot.onclick = () => goTo(i)
        dotsContainer.appendChild(dot)
      })
    }
    if (totalSlides > 0) {
      applyActiveSlideIndex(readerIdx)
      const wrapsForScroll = document.querySelectorAll<HTMLElement>(
        '.stage .study-reader-slide-wrap',
      )
      if (readerIdx < wrapsForScroll.length) {
        wrapsForScroll[readerIdx].scrollIntoView({ block: 'start', behavior: 'auto' })
      }
      attachSlideIntersection()
    }
    return
  }
  if (from === 'reader' && to === 'slide') {
    slideIntersectionObserver?.disconnect()
    slideIntersectionObserver = undefined
    const dotsContainer = document.getElementById('dots')
    if (dotsContainer) dotsContainer.innerHTML = ''
    unwrapReaderSlides()
    await tick()
    const slides = document.querySelectorAll<HTMLElement>('.stage > .slide')
    totalSlides = slides.length
    const slideIdx = totalSlides > 0 ? Math.min(Math.max(0, currentSlide), totalSlides - 1) : 0
    if (dotsContainer) {
      slides.forEach((_, i) => {
        const dot = document.createElement('div')
        dot.className = 'nd' + (i === slideIdx ? ' active' : '')
        dot.onclick = () => goTo(i)
        dotsContainer.appendChild(dot)
      })
    }
    if (totalSlides > 0) {
      currentSlide = slideIdx
      slides.forEach((s, i) => {
        s.classList.remove('exit')
        s.classList.toggle('active', i === slideIdx)
      })
      const cur = slides[slideIdx]
      if (cur) {
        activeSlideId = cur.id
        cur.scrollTop = 0
        cur.scrollLeft = 0
      }
    }
  }
}

let previousLayoutForTransition: 'slide' | 'reader' | undefined = undefined
/** Latest slide↔reader DOM transition; export must await this or the deck stays in the old layout visually. */
let layoutTransitionDone: Promise<void> = Promise.resolve()
$effect(() => {
  if (!browser) return
  const l = layout
  if (previousLayoutForTransition === undefined) {
    previousLayoutForTransition = l
    return
  }
  if (previousLayoutForTransition === l) return
  const from = previousLayoutForTransition
  previousLayoutForTransition = l
  layoutTransitionDone = transitionLayoutFromTo(from, l)
})

function attachSlideIntersection(): void {
  const root = stageEl
  if (!root) return
  slideIntersectionObserver?.disconnect()
  const wraps = root.querySelectorAll<HTMLElement>('.study-reader-slide-wrap')
  slideIntersectionObserver = new IntersectionObserver(
    (entries) => {
      const crossing = entries.filter((e) => e.isIntersecting)
      if (crossing.length === 0) return
      const best = crossing.reduce((a, b) => (a.intersectionRatio >= b.intersectionRatio ? a : b))
      const wrap = best.target as HTMLElement
      const idx = [...wraps].indexOf(wrap)
      if (idx < 0) return
      applyActiveSlideIndex(idx)
    },
    {
      root,
      threshold: [0, 0.05, 0.1, 0.2, 0.35, 0.5, 0.65, 0.8, 1],
      rootMargin: '-38% 0px -38% 0px',
    },
  )
  wraps.forEach((w) => slideIntersectionObserver!.observe(w))
}

function goTo(idx: number): void {
  if (layout === 'reader') {
    const wraps = document.querySelectorAll<HTMLElement>('.stage .study-reader-slide-wrap')
    if (idx < 0 || idx >= totalSlides || idx >= wraps.length) return
    applyActiveSlideIndex(idx)
    wraps[idx].scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }
  const slides = document.querySelectorAll<HTMLElement>('.slide')
  if (idx < 0 || idx >= totalSlides) return
  const exitingSlide = slides[currentSlide]
  exitingSlide.classList.remove('active')
  exitingSlide.classList.add('exit')
  setTimeout(() => exitingSlide.classList.remove('exit'), 360)
  currentSlide = idx
  const activeSlide = slides[currentSlide]
  activeSlide.classList.add('active')
  activeSlide.scrollTop = 0
  activeSlide.scrollLeft = 0
  syncNavDots()
  syncNavButtons()
  activeSlideId = activeSlide.id
  slideIdCopied = false
  if (copyFeedbackTimer !== undefined) {
    clearTimeout(copyFeedbackTimer)
    copyFeedbackTimer = undefined
  }
}

function prev(): void {
  goTo(currentSlide - 1)
}
function next(): void {
  goTo(currentSlide + 1)
}

/** Chart/map lazy mount: in reader mode every slide is active so content preloads off-screen. */
function slideActive(slideId: string): boolean {
  return layout === 'reader' || activeSlideId === slideId
}

async function copySlideId(): Promise<void> {
  if (!browser || !activeSlideId) return
  const text = `slide-${activeSlideId}`
  let copied = false
  try {
    await navigator.clipboard.writeText(text)
    copied = true
  } catch {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('aria-hidden', 'true')
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    try {
      copied = document.execCommand('copy')
    } finally {
      document.body.removeChild(ta)
    }
  }
  if (!copied) return
  clearTimeout(copyFeedbackTimer)
  slideIdCopied = true
  copyFeedbackTimer = setTimeout(() => {
    slideIdCopied = false
    copyFeedbackTimer = undefined
  }, 1600)
}

/** Double rAF: wait until the browser has painted after DOM / Svelte updates. */
function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  )
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Ensure at least `minMs` has elapsed since `start` (export: every slide must fully render). */
async function waitAtLeastMsSince(start: number, minMs: number): Promise<void> {
  const elapsed = performance.now() - start
  if (elapsed < minMs) await sleep(minMs - elapsed)
}

/** Minimum time on each slide during HTML export so layout / charts can settle. */
const EXPORT_MIN_SLIDE_DWELL_MS = 1000

/** How long we accept zero canvases before assuming the deck has no Chart.js charts. */
const EXPORT_NO_CANVAS_GIVEUP_MS = 4000

/** Consecutive polls (all canvases bound, same count) before we treat charts as stable. */
const EXPORT_CHART_STABLE_POLLS = 6

type ChartJsInstance = {
  options?: { animation?: boolean | Record<string, unknown> }
  update?: (mode?: string) => void
  resize?: () => void
  draw?: () => void
}

/** Stop animations and paint a final frame so serialized config matches what’s on screen. */
function finalizeChartJsOnCanvases(
  canvases: HTMLCanvasElement[],
  ChartLib: { getChart?: (el: HTMLCanvasElement) => unknown },
): void {
  for (const canvas of canvases) {
    const raw = ChartLib.getChart?.(canvas)
    if (!raw || typeof raw !== 'object') continue
    const chart = raw as ChartJsInstance
    if (chart.options && typeof chart.options === 'object') {
      chart.options.animation = false
    }
    chart.resize?.()
    chart.update?.('none')
    chart.draw?.()
  }
}

/**
 * Lazy charts mount only when a slide is active. Poll until every canvas on the
 * active slide has a Chart.js instance (or there are no canvases), so export never
 * misses serializing a chart.
 */
async function waitForActiveSlideChartsReady(maxWaitMs = 25000): Promise<void> {
  const ChartLib = (
    window as Window & { Chart?: { getChart?: (el: HTMLCanvasElement) => unknown } }
  ).Chart
  if (!ChartLib?.getChart) return
  const t0 = performance.now()
  while (performance.now() - t0 < maxWaitMs) {
    const activeSlide = document.querySelector<HTMLElement>('.stage .slide.active')
    if (!activeSlide) break
    const canvases = [...activeSlide.querySelectorAll<HTMLCanvasElement>('canvas')]
    if (canvases.length === 0) return
    const pending = canvases.filter((c) => !ChartLib.getChart?.(c))
    if (pending.length === 0) {
      await sleep(350)
      finalizeChartJsOnCanvases(canvases, ChartLib)
      await sleep(120)
      await waitForNextPaint()
      return
    }
    await sleep(100)
  }
}

/**
 * Reader / export: wait until every canvas under `.stage .slide` has a Chart.js instance.
 * Does not return immediately when canvases are still mounting; requires a short stable window
 * so Svelte doesn’t add more canvases mid-export, then finalizes each chart (no animation).
 */
async function waitForAllStageChartsReady(maxWaitMs = 90000): Promise<void> {
  const ChartLib = (
    window as Window & { Chart?: { getChart?: (el: HTMLCanvasElement) => unknown } }
  ).Chart
  if (!ChartLib?.getChart) return
  const t0 = performance.now()
  let zeroCanvasSince: number | null = null
  let stablePolls = 0
  let lastStableKey = ''

  while (performance.now() - t0 < maxWaitMs) {
    const canvases = [
      ...document.querySelectorAll<HTMLElement>('.stage .slide canvas'),
    ] as HTMLCanvasElement[]

    if (canvases.length === 0) {
      if (zeroCanvasSince === null) zeroCanvasSince = performance.now()
      else if (performance.now() - zeroCanvasSince > EXPORT_NO_CANVAS_GIVEUP_MS) return
      await sleep(120)
      continue
    }
    zeroCanvasSince = null

    const pending = canvases.filter((c) => !ChartLib.getChart?.(c))
    const key = `${canvases.length}:${pending.length}`
    if (pending.length === 0 && key === lastStableKey) {
      stablePolls++
      if (stablePolls >= EXPORT_CHART_STABLE_POLLS) {
        finalizeChartJsOnCanvases(canvases, ChartLib)
        await sleep(450)
        await waitForNextPaint()
        return
      }
    } else {
      stablePolls = pending.length === 0 ? 1 : 0
      lastStableKey = key
    }
    await sleep(120)
  }
}

/** Extra settle after reader switch so layout + charts finish before per-slide export. */
const EXPORT_READER_SETTLE_MS = 1200

/** Switch to reader (when parent provides callbacks) so all slides/charts are available for export. */
async function ensureReaderModeForDownload(): Promise<void> {
  if (layout === 'slide' && requestReaderModeForExport) {
    await requestReaderModeForExport()
    await tick()
    await layoutTransitionDone
    await tick()
    await waitForAllStageChartsReady()
    await sleep(EXPORT_READER_SETTLE_MS)
  } else if (layout === 'reader') {
    await tick()
    await layoutTransitionDone
    await tick()
    await waitForAllStageChartsReady()
    await sleep(600)
  }
}

// ── HTML export ───────────────────────────────────────────────────────────
let isDownloading = $state(false)
let downloadMenuOpen = $state(false)
let dlDropdownEl = $state<HTMLDivElement | undefined>(undefined)

async function downloadPDF(): Promise<void> {
  if (isDownloading) return
  isDownloading = true
  const savedSlide = currentSlide
  const wasSlideForExport = layout === 'slide'
  try {
    await ensureReaderModeForDownload()
    if (layout !== 'reader') {
      return
    }
    const wrapped = document.querySelectorAll<HTMLElement>('.stage .study-reader-slide-wrap .slide')
    const slides: HTMLElement[] =
      wrapped.length > 0
        ? [...wrapped]
        : [...document.querySelectorAll<HTMLElement>('.stage > .slide')]
    if (slides.length === 0) return
    const { exportReaderSlidesToPdf } = await import('$lib/utils/exportStudyPdf')
    await exportReaderSlidesToPdf(slides, title)
  } finally {
    isDownloading = false
    if (wasSlideForExport && restoreSlideModeAfterExport) {
      await restoreSlideModeAfterExport()
      await tick()
    }
    goTo(savedSlide)
  }
}

function closeDownloadMenu(): void {
  downloadMenuOpen = false
}

function toggleDownloadMenu(): void {
  if (isDownloading) return
  downloadMenuOpen = !downloadMenuOpen
}

async function pickDownloadHTML(): Promise<void> {
  closeDownloadMenu()
  await downloadHTML()
}

async function pickDownloadPDF(): Promise<void> {
  closeDownloadMenu()
  await downloadPDF()
}

$effect(() => {
  if (!browser || !downloadMenuOpen) return
  const onDocPointerDown = (e: PointerEvent): void => {
    const t = e.target
    if (t instanceof Node && dlDropdownEl?.contains(t)) return
    closeDownloadMenu()
  }
  const id = requestAnimationFrame(() => {
    document.addEventListener('pointerdown', onDocPointerDown, true)
  })
  return () => {
    cancelAnimationFrame(id)
    document.removeEventListener('pointerdown', onDocPointerDown, true)
  }
})

async function downloadHTML(): Promise<void> {
  if (isDownloading) return
  isDownloading = true
  const savedSlide = currentSlide
  const wasSlideForExport = layout === 'slide'
  try {
    await ensureReaderModeForDownload()

    // Extract Chart.js configs — JSON round-trip drops non-serializable callbacks.
    // We rescue:
    //   1. y- and x-axis tick formatters (horizontal bars put formatCount on x.ticks.callback)
    //   2. tooltip label + title → pre-computed per datapoint / category
    type ChartOpts = {
      indexAxis?: string
      scales?: {
        x?: { ticks?: { callback?: unknown; _callbackSrc?: string } }
        y?: { ticks?: { callback?: unknown; _callbackSrc?: string } }
      }
      plugins?: { tooltip?: { callbacks?: Record<string, unknown> } }
    }
    type LiveDataset = { data: number[]; label?: string }
    type LiveData = { labels: string[]; datasets: LiveDataset[] }
    type LiveOpts = ChartOpts
    type ChartInst = { config: { type: string; data: LiveData; options: LiveOpts } }
    const ChartLib = (
      window as Window & { Chart?: { getChart?: (el: HTMLCanvasElement) => ChartInst | undefined } }
    ).Chart
    const chartConfigs: {
      type: string
      data: unknown
      options: ChartOpts
      tooltipLabels?: string[][]
      /** Tooltip title per category (horizontal bar + category titles). */
      tooltipTitles?: string[]
    }[] = []
    let ci = 0

    /**
     * Serialize Chart.js configs for every canvas on this slide. In reader layout all slides
     * stay mounted; in slide layout call once per visited `.slide.active` before navigating away.
     */
    function serializeChartsInSlide(slideEl: HTMLElement): void {
      /** Minified prod bundles rename `formatCount` inside tick fns; standalone HTML only revives `formatCount`. */
      function tickCallbackSrcForExport(fn: (v: number) => string): string {
        const raw = fn.toString()
        const probe = [-0.5, 0, 7, 999, 1500, 45_000, 1_200_000, 40_000_000] as const
        try {
          for (const v of probe) {
            if (fn(v) !== formatCount(v)) return raw
          }
          return '(v) => formatCount(v)'
        } catch {
          return raw
        }
      }

      slideEl.querySelectorAll<HTMLCanvasElement>('canvas').forEach((canvas) => {
        const inst = ChartLib?.getChart?.(canvas)
        if (!inst) return
        canvas.setAttribute('data-ec', String(ci))

        // 1. Rescue x / y tick callbacks (indexAxis: 'y' uses x.ticks.callback for value axis)
        const yTickFn = inst.config.options?.scales?.y?.ticks?.callback
        const xTickFn = inst.config.options?.scales?.x?.ticks?.callback
        const opts: ChartOpts = JSON.parse(JSON.stringify(inst.config.options))
        if (typeof yTickFn === 'function' && opts.scales?.y?.ticks) {
          opts.scales.y.ticks._callbackSrc = tickCallbackSrcForExport(
            yTickFn as (v: number) => string,
          )
        }
        if (typeof xTickFn === 'function' && opts.scales?.x?.ticks) {
          opts.scales.x.ticks._callbackSrc = tickCallbackSrcForExport(
            xTickFn as (v: number) => string,
          )
        }

        // 2. Pre-compute tooltip labels
        const tooltipFn = inst.config.options?.plugins?.tooltip?.callbacks?.label
        const tooltipLabels: string[][] = []
        if (typeof tooltipFn === 'function') {
          inst.config.data.datasets.forEach((dataset, dsIdx) => {
            const row: string[] = []
            dataset.data.forEach((value, dataIdx) => {
              try {
                row.push(
                  tooltipFn({
                    raw: value,
                    label: inst.config.data.labels?.[dataIdx] ?? '',
                    dataset: { label: dataset.label ?? '' },
                    datasetIndex: dsIdx,
                    dataIndex: dataIdx,
                  }) ?? String(value),
                )
              } catch {
                row.push(String(value))
              }
            })
            tooltipLabels.push(row)
          })
        }

        // 3. Pre-compute tooltip titles (BarChart category titles / truncated label restore)
        const titleFn = inst.config.options?.plugins?.tooltip?.callbacks?.title
        let tooltipTitles: string[] | undefined
        if (typeof titleFn === 'function') {
          const lbls = inst.config.data.labels
          const n = Array.isArray(lbls)
            ? lbls.length
            : (inst.config.data.datasets[0]?.data?.length ?? 0)
          const titles: string[] = []
          for (let i = 0; i < n; i++) {
            try {
              const t = titleFn([{ dataIndex: i }] as never)
              titles.push(t == null ? '' : String(t))
            } catch {
              titles.push('')
            }
          }
          if (titles.some((s) => s.length > 0)) tooltipTitles = titles
        }

        chartConfigs.push({
          type: inst.config.type,
          data: JSON.parse(JSON.stringify(inst.config.data)),
          options: opts,
          tooltipLabels: tooltipLabels.length > 0 ? tooltipLabels : undefined,
          tooltipTitles,
        })
        ci++
      })
    }

    const wrappedSlides = document.querySelectorAll<HTMLElement>(
      '.stage .study-reader-slide-wrap .slide',
    )
    const readerSlideList = wrappedSlides.length > 0 ? ([...wrappedSlides] as HTMLElement[]) : null

    if (readerSlideList) {
      // Same order as PDF export: scroll each slide into view, settle, then serialize its charts.
      const ChartLibRefresh = (
        window as Window & { Chart?: { getChart?: (el: HTMLCanvasElement) => unknown } }
      ).Chart
      for (let i = 0; i < readerSlideList.length; i++) {
        const slideEl = readerSlideList[i]
        const slideStart = performance.now()
        slideEl.scrollIntoView({ block: 'start', behavior: 'auto' })
        await tick()
        await waitForNextPaint()
        if (ChartLibRefresh?.getChart) {
          const canvases = [...slideEl.querySelectorAll<HTMLCanvasElement>('canvas')]
          if (canvases.length > 0) {
            finalizeChartJsOnCanvases(canvases, ChartLibRefresh)
            await sleep(200)
            await waitForNextPaint()
          }
        }
        await waitAtLeastMsSince(slideStart, EXPORT_MIN_SLIDE_DWELL_MS)
        serializeChartsInSlide(slideEl)
      }
      readerSlideList[0]?.scrollIntoView({ block: 'start', behavior: 'auto' })
      await tick()
      await waitForNextPaint()
    } else {
      // Slide deck: visit each slide so lazy charts exist before serialize.
      for (let i = 0; i < totalSlides; i++) {
        const slideStart = performance.now()
        goTo(i)
        await tick()
        await waitForNextPaint()
        await waitForActiveSlideChartsReady()
        await waitAtLeastMsSince(slideStart, EXPORT_MIN_SLIDE_DWELL_MS)
        const activeSlide = document.querySelector<HTMLElement>('.stage .slide.active')
        if (activeSlide) serializeChartsInSlide(activeSlide)
      }
      const coverStart = performance.now()
      goTo(0)
      await tick()
      await waitForNextPaint()
      await waitForActiveSlideChartsReady()
      await waitAtLeastMsSince(coverStart, EXPORT_MIN_SLIDE_DWELL_MS)
    }

    // Remove exit class from all slides so the clone doesn't bake in opacity:0 on the
    // previously active slide (e.g. last slide). Otherwise the exported HTML shows a
    // blank final slide until the user navigates away and back.
    document
      .querySelectorAll<HTMLElement>('.stage .slide.exit')
      .forEach((el) => el.classList.remove('exit'))

    // Collect all CSS (Svelte-scoped selectors need the same `svelte-*` classes as the DOM)
    let css = ''
    for (const sheet of document.styleSheets) {
      try {
        for (const rule of sheet.cssRules) css += rule.cssText + '\n'
      } catch {
        /* cross-origin — skip */
      }
    }

    const exportLight = false

    const root =
      presentationEl ?? (document.querySelector('.study-presentation') as HTMLDivElement | null)
    if (!root) return

    // Clone the real shell so the root keeps `study-presentation svelte-…` — compiled CSS
    // targets `.study-presentation.svelte-…` for theme variables and many rules; a plain
    // `study-presentation` div matches almost nothing.
    const shell = root.cloneNode(true) as HTMLElement
    shell.classList.toggle('light-theme', exportLight)
    shell.classList.add('study-reader-presentation')
    shell.querySelector('#bgc')?.remove()
    shell.querySelector('.dl-dropdown')?.remove()
    shell.querySelector('nav.nav')?.remove()
    shell.querySelectorAll('[data-no-export]').forEach((el) => el.remove())
    const shellStage = shell.querySelector<HTMLElement>('.stage')
    if (shellStage) {
      const hasWraps = shellStage.querySelector(':scope > .study-reader-slide-wrap') !== null
      if (!hasWraps) {
        const directSlides = [...shellStage.querySelectorAll<HTMLElement>(':scope > .slide')]
        for (const slide of directSlides) {
          const wrap = document.createElement('div')
          wrap.className = 'study-reader-slide-wrap'
          shellStage.insertBefore(wrap, slide)
          wrap.appendChild(slide)
        }
      }
      shellStage.querySelectorAll<HTMLElement>('.slide').forEach((s) => {
        s.classList.remove('exit', 'active')
      })
    }
    const presentationHtml = shell.outerHTML

    // Escape '<' in JSON so the embedded _ec payload cannot close the HTML script element early
    const chartData = JSON.stringify(chartConfigs).replace(/</g, '\\u003c')

    // ── Embedded vanilla-JS for the standalone file ────────────────────
    const initScript = `(function(){
var pres=document.querySelector('.study-presentation');
var stage=pres&&pres.querySelector('.stage');
var readerSlideIdx=0;
function slideIdFromWrap(w){
  var sl=w&&w.querySelector('.slide');return sl&&sl.id?'slide-'+sl.id:'';
}
function applyReaderSlideActive(){
  if(!stage)return;
  var wraps=[].slice.call(stage.querySelectorAll('.study-reader-slide-wrap'));
  wraps.forEach(function(w,i){
    var sl=w.querySelector('.slide');
    if(sl)sl.classList.toggle('active',i===readerSlideIdx);
  });
}
function syncReaderIndex(){
  if(!stage)return;
  var wraps=[].slice.call(stage.querySelectorAll('.study-reader-slide-wrap'));
  if(!wraps.length)return;
  var stRect=stage.getBoundingClientRect();
  var mid=stRect.top+stRect.height*0.35;
  var found=-1;
  for(var i=0;i<wraps.length;i++){
    var r=wraps[i].getBoundingClientRect();
    if(r.top<=mid&&r.bottom>=mid){found=i;break;}
  }
  if(found<0)found=0;
  readerSlideIdx=found;
  var sidBtn=document.getElementById('slide-id-ref');
  if(sidBtn){
    var label=slideIdFromWrap(wraps[readerSlideIdx]);
    if(label)sidBtn.textContent=label;
  }
  applyReaderSlideActive();
}
function goToReaderSlide(idx){
  var wraps=[].slice.call(stage.querySelectorAll('.study-reader-slide-wrap'));
  if(idx<0||idx>=wraps.length)return;
  readerSlideIdx=idx;
  applyReaderSlideActive();
  wraps[idx].scrollIntoView({behavior:'smooth',block:'start'});
}
syncReaderIndex();
if(stage){stage.addEventListener('scroll',function(){syncReaderIndex();},{passive:true});}
document.querySelectorAll('button.btng').forEach(function(b){
  if(b.textContent.trim().indexOf('Restart')!==-1)b.onclick=function(){if(stage)stage.scrollTop=0;syncReaderIndex();};
});
var sidBtn=document.getElementById('slide-id-ref');
if(sidBtn){sidBtn.addEventListener('click',function(){
  var t=sidBtn.textContent.trim();if(!t||t==='Copied')return;
  function showCopied(){sidBtn.textContent='Copied';setTimeout(syncReaderIndex,1500);}
  if(navigator.clipboard&&navigator.clipboard.writeText){
    navigator.clipboard.writeText(t).then(showCopied).catch(function(){
      var ta=document.createElement('textarea');ta.value=t;ta.style.position='fixed';ta.style.left='-9999px';document.body.appendChild(ta);ta.select();
      try{if(document.execCommand('copy'))showCopied();}finally{document.body.removeChild(ta);}
    });
  }else{
    var ta=document.createElement('textarea');ta.value=t;ta.style.position='fixed';ta.style.left='-9999px';document.body.appendChild(ta);ta.select();
    try{if(document.execCommand('copy'))showCopied();}finally{document.body.removeChild(ta);}
  }
});}
// ── Re-initialise Chart.js charts ──────────────────────────────────────
var cfgs=JSON.parse(document.getElementById('_ec').textContent||'[]');
function ls(src,cb){var s=document.createElement('script');s.src=src;s.onload=cb;document.head.appendChild(s);}
// formatCount mirrors src/lib/utils/report/calculations.ts — needed for serialized tick callbacks
function formatCount(v){
  if(v>=1e12){var t=v/1e12;return(t>=10?t.toFixed(0):t.toFixed(1))+'T';}
  if(v>=1e9){var b=v/1e9;return(b>=10?b.toFixed(0):b.toFixed(1))+'B';}
  if(v>=1e6){var m=v/1e6;return(m>=10?m.toFixed(0):m.toFixed(1))+'M';}
  if(v>=1e3){var k=v/1e3;return(k>=10?k.toFixed(0):k.toFixed(1))+'K';}
  return String(Math.round(v));
}
ls('https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js',function(){
  document.querySelectorAll('[data-ec]').forEach(function(canvas){
    var cfg=cfgs[parseInt(canvas.getAttribute('data-ec'),10)];
    if(!cfg)return;
    try{
      cfg.options=cfg.options||{};
      cfg.options.scales=cfg.options.scales||{};
      // Horizontal bar: value axis tick formatter lives on x.ticks
      var srcX=cfg.options.scales.x&&cfg.options.scales.x.ticks&&cfg.options.scales.x.ticks._callbackSrc;
      if(srcX){try{cfg.options.scales.x.ticks.callback=new Function('formatCount','return('+srcX+')')(formatCount);delete cfg.options.scales.x.ticks._callbackSrc;}catch(e){}}
      var srcY=cfg.options.scales.y&&cfg.options.scales.y.ticks&&cfg.options.scales.y.ticks._callbackSrc;
      if(srcY){try{cfg.options.scales.y.ticks.callback=new Function('formatCount','return('+srcY+')')(formatCount);delete cfg.options.scales.y.ticks._callbackSrc;}catch(e){}}
      if(cfg.tooltipLabels&&cfg.tooltipLabels.length>0){
        var tl=cfg.tooltipLabels;
        cfg.options.plugins=cfg.options.plugins||{};
        cfg.options.plugins.tooltip=cfg.options.plugins.tooltip||{};
        cfg.options.plugins.tooltip.callbacks=cfg.options.plugins.tooltip.callbacks||{};
        cfg.options.plugins.tooltip.callbacks.label=function(c){var row=tl[c.datasetIndex];return row?row[c.dataIndex]:String(c.raw);};
      }
      if(cfg.tooltipTitles&&cfg.tooltipTitles.length>0){
        var tts=cfg.tooltipTitles;
        cfg.options.plugins=cfg.options.plugins||{};
        cfg.options.plugins.tooltip=cfg.options.plugins.tooltip||{};
        cfg.options.plugins.tooltip.callbacks=cfg.options.plugins.tooltip.callbacks||{};
        cfg.options.plugins.tooltip.callbacks.title=function(items){var it=items&&items[0];var i=it!==undefined&&it!==null?it.dataIndex:undefined;return i!==undefined&&tts[i]!=null?tts[i]:'';};
      }
      new window.Chart(canvas,{type:cfg.type,data:cfg.data,options:cfg.options});
    }catch(err){console.warn('Study HTML export: chart init failed',err);}
  });
});
// ── Re-attach choropleth hover (SVG paths cloned from live DOM) ────────
var mapSvg=document.getElementById('us-map-svg');
if(mapSvg){
  var slide4b=mapSvg.closest('#s4b');
  var side=slide4b&&slide4b.querySelector('.choro-side');
  var nameEl=side&&side.querySelector('.ct');
  var valEl=nameEl&&nameEl.nextElementSibling;
  mapSvg.querySelectorAll('path').forEach(function(path){
    var t=path.querySelector('title');
    var parts=(t?t.textContent:'').split(': ');
    var sn=parts[0]||'';var sv=parts[1]||'—';
    var of=path.getAttribute('fill');
    path.addEventListener('mouseenter',function(){
      path.setAttribute('fill','#22d4dc');
      path.setAttribute('stroke','#22d4dc');
      path.setAttribute('stroke-width','1.5');
      if(nameEl)nameEl.textContent=sn;
      if(valEl)valEl.textContent=sv;
    });
    path.addEventListener('mouseleave',function(){
      path.setAttribute('fill',of);
      path.setAttribute('stroke','#071219');
      path.setAttribute('stroke-width','0.5');
      if(nameEl)nameEl.textContent='Hover a state';
      if(valEl)valEl.textContent='—';
    });
  });
}
if(stage){
  document.addEventListener('keydown',function(e){
    var wraps=[].slice.call(stage.querySelectorAll('.study-reader-slide-wrap'));
    if(!wraps.length)return;
    if(e.key==='ArrowRight'||e.key==='ArrowDown'||e.key===' '||e.key==='Enter'){
      e.preventDefault();
      if(readerSlideIdx<wraps.length-1)goToReaderSlide(readerSlideIdx+1);
    }else if(e.key==='ArrowLeft'||e.key==='ArrowUp'){
      e.preventDefault();
      if(readerSlideIdx>0)goToReaderSlide(readerSlideIdx-1);
    }
  });
}
})();`

    const slug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
    const now = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    const dateTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}`
    const bodyStyle = exportLight
      ? `background:#eef7f7;font-family:'Space Grotesk',sans-serif;color:#0a1211;overflow:hidden;`
      : `background:#030b0a;font-family:'Space Grotesk',sans-serif;color:#fff;overflow:hidden;`
    const bodyClass = exportLight ? ' study-light-export' : ''
    const html = [
      '<!DOCTYPE html>',
      '<html lang="en" style="overflow:hidden;">',
      '<head>',
      '  <meta charset="UTF-8"/>',
      '  <meta name="viewport" content="width=device-width,initial-scale=1.0"/>',
      '  <base href="https://www.imxresearch.com/">',
      `  <title>${title}</title>`,
      '  <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@400;500&family=Syne:wght@400;600;700;800&family=Space+Grotesk:wght@300;400;500;600;700&display=swap" rel="stylesheet"/>',
      '  <' + `style>\n${css}\n  </` + 'style>',
      '</head>',
      `<body class="${bodyClass.trim()}" style="${bodyStyle}">`,
      `<script id="_ec" type="application/json">${chartData}<\/script>`,
      presentationHtml,
      `<script>${initScript}<\/script>`,
      '</body>',
      '</html>',
    ].join('\n')

    // Clean up temporary attributes on live canvases
    document.querySelectorAll<HTMLElement>('.stage [data-ec]').forEach((el) => {
      el.removeAttribute('data-ec')
    })

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${slug}_${dateTime}.html`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  } finally {
    isDownloading = false
    if (wasSlideForExport && restoreSlideModeAfterExport) {
      await restoreSlideModeAfterExport()
      await tick()
    }
    goTo(savedSlide)
  }
}

// ── Particle animation ────────────────────────────────────────────────────
interface Particle {
  x: number
  y: number
  r: number
  vx: number
  vy: number
  a: number
}

let cancelParticleAnim = () => {}
let removeKeyListener = () => {}
let removeParentNavMessageListener = () => {}
let removeResizeListener = () => {}

/** Presentation root; used for embed viewport sizing (particles / background canvas). */
let presentationEl: HTMLDivElement | undefined

function embedViewportSize(): { w: number; h: number } {
  const el = presentationEl
  if (el) {
    const r = el.getBoundingClientRect()
    if (r.width > 2 && r.height > 2) {
      return { w: r.width, h: r.height }
    }
    if (el.clientWidth > 0 && el.clientHeight > 0) {
      return { w: el.clientWidth, h: el.clientHeight }
    }
  }
  const vv = window.visualViewport
  if (vv && vv.width > 0 && vv.height > 0) {
    return { w: vv.width, h: vv.height }
  }
  const de = document.documentElement
  if (de.clientWidth > 0 && de.clientHeight > 0) {
    return { w: de.clientWidth, h: de.clientHeight }
  }
  return { w: window.innerWidth, h: window.innerHeight }
}

async function initPresentation(): Promise<void> {
  if (layout === 'reader') {
    await tick()
    wrapReaderSlides()
    const slides = document.querySelectorAll<HTMLElement>('.stage .study-reader-slide-wrap .slide')
    totalSlides = slides.length
    const dotsContainer = document.getElementById('dots')!
    slides.forEach((_, i) => {
      const dot = document.createElement('div')
      dot.className = 'nd' + (i === 0 ? ' active' : '')
      dot.onclick = () => goTo(i)
      dotsContainer.appendChild(dot)
    })
    if (totalSlides > 0) {
      currentSlide = 0
      applyActiveSlideIndex(0)
      attachSlideIntersection()
    }
    syncNavButtons()
  } else {
    await tick()
    const root = stageEl ?? document
    const slides = root.querySelectorAll<HTMLElement>('.slide')
    totalSlides = slides.length
    const dotsContainer = document.getElementById('dots')!
    slides.forEach((_, i) => {
      const dot = document.createElement('div')
      dot.className = 'nd' + (i === 0 ? ' active' : '')
      dot.onclick = () => goTo(i)
      dotsContainer.appendChild(dot)
    })
    if (totalSlides > 0) {
      currentSlide = 0
      activeSlideId = slides[0].id
    }
    syncNavButtons()
  }

  // Load Chart.js from CDN (chart components await `chartJsGate.ready` so effects re-run after this)
  try {
    await new Promise<void>((resolve, reject) => {
      if ((window as Window & { Chart?: unknown }).Chart) {
        chartJsGate.ready = true
        resolve()
        return
      }
      const s = document.createElement('script')
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js'
      s.onload = () => {
        chartJsGate.ready = true
        resolve()
      }
      s.onerror = reject
      document.head.appendChild(s)
    })
  } catch {
    console.warn('Study presentation: Chart.js failed to load from CDN')
  }

  document.documentElement.style.overflow = 'hidden'
  document.body.style.overflow = 'hidden'

  const bgCanvas = document.getElementById('bgc') as HTMLCanvasElement
  const bgCtx = bgCanvas.getContext('2d')!
  /**
   * iOS Safari: the bottom toolbar (share / refresh / tabs) sits in the layout viewport but
   * outside the visual viewport, so `position:fixed; bottom:0` UI ends up underneath it.
   * Expose the gap as `--study-vv-bottom-inset` for nav and footer chrome.
   *
   * In a cross-origin parent iframe, `window.visualViewport` inside the iframe often does *not*
   * reflect the top-level Safari UI — the parent embed posts `imx-study-parent-vv-bottom`;
   * we merge with Math.max.
   */
  let parentPostedVvBottomPx: number | null = null
  function syncVisualViewportBottomInset(): void {
    const root = document.documentElement
    let internal = 0
    const vv = window.visualViewport
    if (vv) {
      internal = Math.max(0, window.innerHeight - vv.offsetTop - vv.height)
    }
    const combined =
      parentPostedVvBottomPx != null ? Math.max(internal, parentPostedVvBottomPx) : internal
    root.style.setProperty('--study-vv-bottom-inset', `${combined}px`)
  }
  const onViewportResize = (): void => {
    syncVisualViewportBottomInset()
    const { w, h } = embedViewportSize()
    bgCanvas.width = w
    bgCanvas.height = h
  }
  onViewportResize()
  /* Safari often updates visual viewport after first layout frame (toolbar height). */
  requestAnimationFrame(() => requestAnimationFrame(onViewportResize))
  window.addEventListener('resize', onViewportResize)
  const vv = window.visualViewport
  if (vv) {
    vv.addEventListener('resize', onViewportResize)
    vv.addEventListener('scroll', onViewportResize)
  }
  removeResizeListener = () => {
    window.removeEventListener('resize', onViewportResize)
    if (vv) {
      vv.removeEventListener('resize', onViewportResize)
      vv.removeEventListener('scroll', onViewportResize)
    }
    document.documentElement.style.removeProperty('--study-vv-bottom-inset')
  }

  const particles: Particle[] = (() => {
    const { w, h } = embedViewportSize()
    return Array.from({ length: 50 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 1.2 + 0.3,
      vx: (Math.random() - 0.5) * 0.11,
      vy: (Math.random() - 0.5) * 0.11,
      a: Math.random() * 0.28 + 0.06,
    }))
  })()

  let animFrameId = 0
  const animateParticles = (): void => {
    bgCtx.clearRect(0, 0, bgCanvas.width, bgCanvas.height)
    particles.forEach((p) => {
      p.x += p.vx
      p.y += p.vy
      if (p.x < 0) p.x = bgCanvas.width
      if (p.x > bgCanvas.width) p.x = 0
      if (p.y < 0) p.y = bgCanvas.height
      if (p.y > bgCanvas.height) p.y = 0
      bgCtx.beginPath()
      bgCtx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
      bgCtx.fillStyle = `rgba(17,160,165,${p.a})`
      bgCtx.fill()
    })
    animFrameId = requestAnimationFrame(animateParticles)
  }
  animateParticles()
  cancelParticleAnim = () => cancelAnimationFrame(animFrameId)

  const handleKey = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      next()
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault()
      prev()
    }
  }
  document.addEventListener('keydown', handleKey)
  removeKeyListener = () => document.removeEventListener('keydown', handleKey)

  /** Parent page (e.g. Webflow) — nav + Safari visual inset via postMessage */
  if (window.parent !== window) {
    const handleParentNavMessage = (e: MessageEvent): void => {
      if (e.source !== window.parent) return
      const d = e.data
      if (!d || typeof d !== 'object') return
      const msg = d as { type?: unknown; action?: unknown; index?: unknown; px?: unknown }
      if (msg.type === 'imx-study-parent-vv-bottom' && typeof msg.px === 'number') {
        parentPostedVvBottomPx = Math.max(0, msg.px)
        syncVisualViewportBottomInset()
        return
      }
      if (msg.type !== 'imx-study-nav') return
      if (msg.action === 'next') next()
      else if (msg.action === 'prev') prev()
      else if (msg.action === 'goTo' && typeof msg.index === 'number') goTo(msg.index)
    }
    window.addEventListener('message', handleParentNavMessage)
    removeParentNavMessageListener = () =>
      window.removeEventListener('message', handleParentNavMessage)
  }
}

onMount(() => {
  initPresentation()
  return () => {
    slideIntersectionObserver?.disconnect()
    slideIntersectionObserver = undefined
    removeKeyListener()
    removeParentNavMessageListener()
    removeResizeListener()
    cancelParticleAnim()
    document.documentElement.style.overflow = ''
    document.body.style.overflow = ''
  }
})
</script>

<svelte:head>
  <title>{title}</title>
  {#if lightTheme}
    <meta name="color-scheme" content="light" />
  {/if}
  <link
    href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@400;500&family=Syne:wght@400;600;700;800&family=Space+Grotesk:wght@300;400;500;600;700&display=swap"
    rel="stylesheet"
  />
</svelte:head>

<div
  class="study-presentation"
  class:study-reader-presentation={layout === 'reader'}
  class:light-theme={lightTheme}
  bind:this={presentationEl}
>
  <canvas
    id="bgc"
    style="position:fixed;inset:0;pointer-events:none;z-index:0;opacity:{lightTheme ? 0.18 : 0.33};"
  ></canvas>
  <div class="progress">
    <div class="pf" id="pfill" style:width={`${navProgressPct}%`}></div>
  </div>

  <StudyPresentationLogoHeader />

  <div class="stage" bind:this={stageEl}>
    {@render children({ goTo, activeSlideId, slideActive })}
  </div>

  <div class="study-top-content-shield" aria-hidden="true"></div>

  <nav class="nav" id="nav-bar" style="opacity:0;pointer-events:none">
    <button class="nb" id="bp">&larr;</button>
    <div class="nds" id="dots"></div>
    <button class="nb" id="bn">&rarr;</button>
    <span class="nc" id="ctr">{navCounterLabel}</span>
  </nav>

  {#if devMode}
    <button
      type="button"
      class="slide-id-ref"
      class:slide-id-ref--show-feedback={slideIdCopied}
      id="slide-id-ref"
      title="Copy slide id"
      aria-label={slideIdCopied
        ? 'Copied to clipboard'
        : activeSlideId
          ? `Copy slide id: slide-${activeSlideId}`
          : 'Slide id'}
      onclick={copySlideId}
    >
      {#if slideIdCopied}
        <span class="slide-id-ref__copied" aria-live="polite">Copied</span>
      {:else if activeSlideId}
        slide-{activeSlideId}
      {/if}
    </button>
  {/if}

  <div class="mob-badges">
    <a
      class="bg"
      href="https://www.businesswire.com/news/home/20260108466753/en/IMX-Data-Achieves-SOC-2-Certification-Reinforcing-Commitment-to-Data-Security-and-Trust"
      target="_blank"
      rel="noopener noreferrer"
    >
      <svg
        width="9"
        height="9"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        ><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline
          points="9 12 11 14 15 10"
        /></svg
      >SOC 2 Certified
    </a>
    <a class="bg" href="https://www.imxresearch.com/" target="_blank" rel="noopener noreferrer"
      >imxresearch.com</a
    >
    <div class="bg">&copy; 2026 IMX Data</div>
  </div>

  {#if meta}
    <aside class="study-citation-chip bg" aria-label="Citation information">
      Updated <time datetime={meta.updatedDate}>{formatShortDate(meta.updatedDate)}</time>
    </aside>
  {/if}

  {#if devMode}
    <div class="dl-dropdown" bind:this={dlDropdownEl} class:dl-dropdown--open={downloadMenuOpen}>
      <button
        type="button"
        class="dl-btn"
        onclick={toggleDownloadMenu}
        disabled={isDownloading}
        aria-expanded={downloadMenuOpen}
        aria-haspopup="menu"
        title={isDownloading ? 'Generating…' : 'Download presentation'}
      >
        {#if isDownloading}
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            class="animate-spin"
            ><path
              d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"
            /></svg
          >
          <span>Generating…</span>
        {:else}
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            ><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline
              points="7 10 12 15 17 10"
            /><line x1="12" y1="15" x2="12" y2="3" /></svg
          >
          <span>Download</span>
          <svg
            class="dl-btn__chev"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg
          >
        {/if}
      </button>
      {#if downloadMenuOpen && !isDownloading}
        <div class="dl-dropdown__menu" role="menu" aria-label="Download format">
          <button
            type="button"
            class="dl-dropdown__item"
            role="menuitem"
            onclick={() => void pickDownloadHTML()}
          >
            HTML
          </button>
          <button
            type="button"
            class="dl-dropdown__item"
            role="menuitem"
            onclick={() => void pickDownloadPDF()}
          >
            PDF
          </button>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
.study-presentation {
  position: fixed;
  inset: 0;
  overflow: hidden;
  --ink: #030b0a;
  --g: #11a0a5;
  --gb: #22d4dc;
  --gdim: rgba(17, 160, 165, 0.14);
  --gbr: rgba(17, 160, 165, 0.42);
  --amb: #ffd166;
  --red: #e8634a;
  --pur: #a78bfa;
  --blu: #38bdf8;
  --mu: rgba(255, 255, 255, 0.58);
  --t2: rgba(255, 255, 255, 0.82);
  --bo: rgba(255, 255, 255, 0.13);
  --su: rgba(255, 255, 255, 0.06);
  --su2: rgba(255, 255, 255, 0.11);
  --slide-fg: #ffffff;
  --slide-bg-solid: #030b0a;
  --cover-sub: rgba(255, 255, 255, 0.85);
  --table-surface: rgba(0, 0, 0, 0.12);
  --row-stripe: rgba(255, 255, 255, 0.02);
  --bar-track: rgba(255, 255, 255, 0.07);
  --bar-track-lg: rgba(255, 255, 255, 0.06);
  --rank-row-bg-strong: rgba(255, 255, 255, 0.055);
  --rank-row-bg-soft: rgba(255, 255, 255, 0.025);
  --rank-border-strong: rgba(255, 255, 255, 0.1);
  --rank-border-soft: rgba(255, 255, 255, 0.05);
  --card-alt-bg: rgba(0, 0, 0, 0.18);
  --table-border-t: rgba(255, 255, 255, 0.08);
  --chart-tick: rgba(255, 255, 255, 0.52);
  --chart-grid: rgba(255, 255, 255, 0.07);
  --chart-crosshair: rgba(255, 255, 255, 0.28);
  --chart-legend: rgba(255, 255, 255, 0.72);
  /* Opt-in, for charts whose legend carries meaning the reader has to resolve
     (multi-series lines) rather than merely label a single trace. */
  --chart-legend-strong: #ffffff;
  /*
   * Diverging choropleth ramp (low → middle → high). Semantic, and deliberately separate
   * from `colorPalette`: these encode magnitude, not series identity. Tuned per theme — the
   * dark set sits on a near-black slide, the light set on #eef7f7, and a stop that reads on
   * one is close to invisible on the other.
   */
  --chart-diverging-low: #8b1a1a;
  --chart-diverging-mid: #808080;
  --chart-diverging-high: #22d3ee;
  --chart-tooltip-bg: #091c1e;
  --chart-tooltip-title: #ffffff;
  --chart-tooltip-body: rgba(255, 255, 255, 0.64);
  --chart-tooltip-border: rgba(255, 255, 255, 0.1);
  /*
   * Fixed logo (`.logo` top/left + `#logo` width on desktop — see media queries below).
   * Used to inset slide content past the wordmark when the slide is short + wide enough for overlap.
   */
  --study-logo-inset-left: 1.6rem;
  --study-logo-mark-width: 12.5rem;
  --study-logo-clearance-extra: 1.25rem;
  --study-logo-clearance-left: max(
    2.75rem,
    calc(
      var(--study-logo-inset-left) + var(--study-logo-mark-width) +
        var(--study-logo-clearance-extra)
    )
  );
}
.study-presentation.light-theme {
  --ink: #eef7f7;
  --g: #0d8f94;
  --gb: #1ab8c0;
  --gdim: rgba(17, 160, 165, 0.12);
  --gbr: rgba(17, 160, 165, 0.35);
  --mu: rgba(10, 18, 17, 0.55);
  --t2: rgba(10, 18, 17, 0.88);
  --bo: rgba(0, 0, 0, 0.1);
  --su: rgba(0, 0, 0, 0.04);
  --su2: rgba(0, 0, 0, 0.08);
  --slide-fg: #0a1211;
  --slide-bg-solid: #eef7f7;
  --cover-sub: rgba(10, 18, 17, 0.82);
  --table-surface: rgba(0, 0, 0, 0.06);
  --row-stripe: rgba(0, 0, 0, 0.035);
  --bar-track: rgba(0, 0, 0, 0.1);
  --bar-track-lg: rgba(0, 0, 0, 0.08);
  --rank-row-bg-strong: rgba(0, 0, 0, 0.06);
  --rank-row-bg-soft: rgba(0, 0, 0, 0.03);
  --rank-border-strong: rgba(0, 0, 0, 0.12);
  --rank-border-soft: rgba(0, 0, 0, 0.07);
  --card-alt-bg: rgba(0, 0, 0, 0.06);
  --table-border-t: rgba(0, 0, 0, 0.08);
  --chart-tick: rgba(10, 18, 17, 0.62);
  --chart-grid: rgba(0, 0, 0, 0.1);
  --chart-crosshair: rgba(10, 18, 17, 0.28);
  --chart-legend: rgba(10, 18, 17, 0.78);
  --chart-legend-strong: #0a1211;
  /* Darkened ends and a heavier middle: on a light ground the dark-theme cyan falls to
     1.66:1 and the high end of the scale disappears. These hold ~4.4–5.9:1. */
  --chart-diverging-low: #b91c1c;
  --chart-diverging-mid: #6b7280;
  --chart-diverging-high: #0e7490;
  --chart-tooltip-bg: #f4fbfb;
  --chart-tooltip-title: #0a1211;
  --chart-tooltip-body: rgba(10, 18, 17, 0.72);
  --chart-tooltip-border: rgba(0, 0, 0, 0.12);
}
.study-presentation.light-theme .progress {
  background: rgba(0, 0, 0, 0.06);
}
.study-presentation.light-theme .pf {
  box-shadow: 0 0 10px rgba(34, 212, 220, 0.4);
}
.study-presentation.light-theme .nav {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.study-presentation.light-theme .nb {
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(0, 0, 0, 0.04);
  color: rgba(10, 18, 17, 0.65);
}
.study-presentation.light-theme .nb:hover {
  background: rgba(17, 160, 165, 0.12);
}
.study-presentation.light-theme :global(.nd) {
  background: rgba(0, 0, 0, 0.15);
}
.study-presentation.light-theme .nc {
  color: rgba(10, 18, 17, 0.42);
}
.study-presentation.light-theme .dl-btn {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: rgba(10, 18, 17, 0.55);
}
:global(body.study-light-export) {
  --slide-fg: #0a1211;
}
.study-presentation.light-theme :global(.disp),
.study-presentation.light-theme :global(.hl) {
  color: var(--slide-fg);
}
.study-presentation.light-theme :global(.ca h4),
.study-presentation.light-theme :global(.ct) {
  color: var(--slide-fg);
}
.study-presentation.light-theme :global(.tt strong) {
  color: var(--slide-fg);
}
.study-presentation.light-theme :global(.hb) {
  background: linear-gradient(135deg, rgba(17, 160, 165, 0.1) 0%, rgba(17, 160, 165, 0.04) 100%);
}
.study-presentation.light-theme :global(.hb p) {
  color: rgba(10, 18, 17, 0.75);
}
.study-presentation.light-theme :global(.hb p strong) {
  color: var(--slide-fg);
}
.study-presentation.light-theme :global(.bg) {
  color: rgba(10, 18, 17, 0.45);
}
:global(*) {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
.progress {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 300;
  height: 2px;
  background: rgba(255, 255, 255, 0.06);
}
.pf {
  height: 100%;
  background: linear-gradient(90deg, var(--g), var(--gb));
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 0 12px rgba(34, 212, 220, 0.6);
}
/*
 * Scrollable slide layouts (≤1024px): solid slide-colored band + soft mask so rising content
 * fades out before it can read through the fixed logo area (logo stays fully opaque above this).
 */
.study-top-content-shield {
  display: none;
}
@media (max-width: 1024px) {
  .study-presentation .study-top-content-shield {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 280;
    pointer-events: none;
    height: calc(7.25rem + env(safe-area-inset-top, 0px));
    background: var(--slide-bg-solid);
    mask-image: linear-gradient(
      to bottom,
      #000 0%,
      #000 24%,
      rgba(0, 0, 0, 0.55) 52%,
      rgba(0, 0, 0, 0.12) 78%,
      transparent 100%
    );
    -webkit-mask-image: linear-gradient(
      to bottom,
      #000 0%,
      #000 24%,
      rgba(0, 0, 0, 0.55) 52%,
      rgba(0, 0, 0, 0.12) 78%,
      transparent 100%
    );
  }
}
.stage {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
:global(.slide) {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4.25rem 2.75rem calc(9rem + var(--study-vv-bottom-inset, 0px));
  opacity: 0;
  pointer-events: none;
  transform: translateY(26px);
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
  background: var(--slide-bg-solid);
}
:global(.slide.slide-extra-bottom) {
  padding-bottom: calc(12rem + var(--study-vv-bottom-inset, 0px));
}

/*
 * Desktop + short viewport: fixed logo overlaps vertically centered slide content; fixed nav needs
 * scroll + bottom padding.
 * Narrow-ish widths: inset past wordmark (centered `.inn` can sit under the logo — see GLP-1/ADC s4b).
 * Narrow desktop (≤~1580px): centered `.inn` can sit under the wordmark — extra left gutter on every slide.
 * Wide + short: top alignment + scroll; horizontal clearance often unnecessary (content clears the logo).
 */
@media (min-width: 1025px) and (max-height: 900px) {
  .study-presentation :global(.slide) {
    align-items: flex-start;
    justify-content: center;
    padding-top: calc(6rem + env(safe-area-inset-top, 0px));
    padding-right: 2.75rem;
    padding-bottom: calc(12.5rem + var(--study-vv-bottom-inset, 0px));
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .study-presentation :global(.slide.slide-extra-bottom) {
    padding-bottom: calc(13rem + var(--study-vv-bottom-inset, 0px));
  }
  .study-presentation :global(.slide::-webkit-scrollbar) {
    display: none;
  }
}
/*
 * Logo clearance. Between these widths the fixed wordmark overlaps the slide, so content is inset
 * past it. The inset is applied to BOTH sides: padding only on the left reserves the space but
 * also shifts every slide ~245px right of centre, which reads as a large asymmetric left margin
 * on medium screens. Mirroring it keeps the wordmark guard while leaving content centred, since
 * `.slide` centres its children within the remaining box.
 */
@media (min-width: 1025px) and (max-width: 1580px) {
  .study-presentation :global(.slide) {
    padding-left: var(--study-logo-clearance-left);
    padding-right: var(--study-logo-clearance-left);
  }
}

/* Choropleth slide: slightly smaller title + map so the block fits under logo + nav in short viewports */
@media (min-width: 1025px) and (max-height: 900px) {
  .study-presentation :global(.study-s4b-choro-shell) {
    transform: scale(0.93);
    transform-origin: top center;
  }
  .study-presentation :global(.slide:has(.study-s4b-choro-shell) .ey) {
    margin-bottom: 0.35rem;
    font-size: 0.58rem;
  }
  .study-presentation :global(.slide:has(.study-s4b-choro-shell) .hl) {
    margin-bottom: 0.5rem;
    font-size: clamp(1.22rem, 2.15vw, 1.62rem);
    line-height: 1.14;
  }
}

:global(.slide.active) {
  opacity: 1;
  pointer-events: all;
  transform: translateY(0);
}
:global(.slide.exit) {
  opacity: 0;
  transform: translateY(-26px);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}
:global(.inn) {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1020px;
}
:global(.sg > *) {
  opacity: 0;
  transform: translateY(14px);
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}
:global(.slide.active .sg > *:nth-child(1)) {
  opacity: 1;
  transform: none;
  transition-delay: 0.06s;
}
:global(.slide.active .sg > *:nth-child(2)) {
  opacity: 1;
  transform: none;
  transition-delay: 0.17s;
}
:global(.slide.active .sg > *:nth-child(3)) {
  opacity: 1;
  transform: none;
  transition-delay: 0.29s;
}
:global(.slide.active .sg > *:nth-child(4)) {
  opacity: 1;
  transform: none;
  transition-delay: 0.41s;
}
:global(.slide.active .sg > *:nth-child(5)) {
  opacity: 1;
  transform: none;
  transition-delay: 0.53s;
}
:global(.slide.active .sg > *:nth-child(6)) {
  opacity: 1;
  transform: none;
  transition-delay: 0.65s;
}
:global(.ey) {
  font-family: 'DM Mono', monospace;
  font-size: 0.6rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--g);
  margin-bottom: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.55rem;
}
:global(.ey::before) {
  content: '';
  display: inline-block;
  width: 20px;
  height: 1px;
  background: var(--g);
}
:global(.disp) {
  font-family: 'DM Serif Display', serif;
  font-size: clamp(2.5rem, 5.2vw, 4.6rem);
  line-height: 1.07;
  letter-spacing: -0.025em;
  color: #fff;
}
:global(.disp em) {
  font-style: italic;
  color: var(--g);
}
:global(.hl) {
  font-family: 'DM Serif Display', serif;
  font-size: clamp(1.55rem, 2.9vw, 2.4rem);
  line-height: 1.13;
  letter-spacing: -0.02em;
  color: #fff;
}
:global(.hl em) {
  font-style: italic;
  color: var(--g);
}
:global(.bd) {
  font-size: 0.9rem;
  line-height: 1.75;
  color: var(--t2);
}
:global(.mn) {
  font-family: 'DM Mono', monospace;
  font-size: 0.71rem;
  color: var(--g);
  letter-spacing: 0.06em;
}
:global(.tc) {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 2rem;
  align-items: start;
}
:global(.tc.w) {
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
}
:global(.ttc) {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.82rem;
}
:global(.hr) {
  height: 1px;
  background: var(--bo);
  margin: 1rem 0;
}
:global(.ca) {
  background: var(--su);
  border: 1px solid var(--bo);
  border-radius: 10px;
  padding: 1.15rem 1.1rem;
  position: relative;
  overflow: hidden;
  transition:
    border-color 0.2s,
    background 0.2s;
}
:global(.ca:hover) {
  background: var(--su2);
  border-color: var(--gbr);
}
:global(.ca::before) {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--g);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s;
}
:global(.ca:hover::before) {
  transform: scaleX(1);
}
:global(.ca h4) {
  font-size: 0.81rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.28rem;
}
:global(.ca p) {
  font-size: 0.73rem;
  color: var(--t2);
  line-height: 1.58;
}
:global(.ca .ic) {
  font-size: 1.2rem;
  margin-bottom: 0.52rem;
}
:global(.ca .vl) {
  font-family: 'DM Serif Display', serif;
  font-size: 1.5rem;
  color: var(--g);
  margin-top: 0.32rem;
  line-height: 1;
}
:global(.ca .vs) {
  font-size: 0.64rem;
  color: var(--mu);
  margin-top: 0.16rem;
}
:global(.sg4) {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.85rem;
}
:global(.sb) {
  text-align: center;
  padding: 1.2rem 0.9rem;
  border-radius: 10px;
  background: var(--su);
  border: 1px solid var(--bo);
}
:global(.sb .n) {
  font-family: 'DM Serif Display', serif;
  font-size: 1.95rem;
  color: var(--g);
  line-height: 1;
}
:global(.sb .l) {
  font-size: 0.61rem;
  color: var(--mu);
  text-transform: uppercase;
  letter-spacing: 0.09em;
  margin-top: 0.38rem;
  font-weight: 500;
  line-height: 1.38;
}
:global(.hb) {
  background: linear-gradient(135deg, rgba(17, 160, 165, 0.16) 0%, rgba(7, 69, 83, 0.09) 100%);
  border: 1px solid var(--gbr);
  border-radius: 10px;
  padding: 1.1rem 1.35rem;
}
:global(.hb p) {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.74);
  line-height: 1.69;
}
:global(.hb p strong) {
  color: #fff;
}
:global(.cc) {
  background: var(--su);
  border: 1px solid var(--bo);
  border-radius: 10px;
  padding: 0.95rem 1.05rem 0.88rem;
}
:global(.ct) {
  font-size: 0.74rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.16rem;
}
:global(.cs) {
  font-size: 0.63rem;
  color: var(--mu);
  margin-bottom: 0.78rem;
}
:global(.lg) {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.55rem;
}
:global(.ld) {
  display: flex;
  align-items: center;
  gap: 0.28rem;
  font-size: 0.67rem;
  color: var(--mu);
}
:global(.ldd) {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
:global(.tli) {
  display: flex;
  gap: 0.88rem;
  padding: 0.72rem 0.95rem;
  border-radius: 8px;
  border: 1px solid var(--bo);
  background: var(--su);
  margin-bottom: 0.48rem;
  transition: background 0.2s;
}
:global(.tli:hover) {
  background: var(--su2);
}
:global(.ty) {
  font-family: 'DM Mono', monospace;
  font-size: 0.69rem;
  color: var(--g);
  min-width: 50px;
  padding-top: 0.04rem;
}
:global(.tt) {
  font-size: 0.76rem;
  color: var(--t2);
  line-height: 1.54;
}
:global(.tt strong) {
  color: #fff;
}
:global(.bg) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  background: var(--su);
  border: 1px solid var(--bo);
  border-radius: 0.4rem;
  padding: 0.26rem 0.58rem;
  font-size: 0.6rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.48);
  letter-spacing: 0.07em;
  text-transform: uppercase;
  text-align: center;
  text-decoration: none;
}
:global(a.bg) {
  pointer-events: all;
  cursor: pointer;
}
/*
 * Ranked volume bars. The grid lives on the LIST, not the row, so the label column is sized once
 * from the longest label and every bar track starts at the same x — with `display:flex` per row
 * each label sized to its own text and the bars stepped raggedly. `max-content` on the label column
 * lets short-label studies stay tight while long ones (agent names) still align; the track column
 * takes the rest. `.dbr` keeps a `display:contents` fallback so a row that is not inside `.dbrs`
 * still renders sensibly.
 */
:global(.dbrs) {
  display: grid;
  grid-template-columns: minmax(104px, max-content) minmax(0, 1fr) minmax(38px, max-content);
  align-items: center;
  column-gap: 0.68rem;
  row-gap: 0.48rem;
}
:global(.dbrs) > :global(.dbr) {
  display: contents;
}
:global(.dbr) {
  display: flex;
  align-items: center;
  gap: 0.68rem;
  margin-bottom: 0.48rem;
}
:global(.dbl) {
  font-size: 0.67rem;
  color: var(--t2);
  min-width: 104px;
  font-family: 'DM Mono', monospace;
}
:global(.dbrs) > :global(.dbr) > :global(.dbl) {
  min-width: 0;
}
:global(.dbt) {
  flex: 1;
  height: 5px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
}
:global(.dbf) {
  height: 100%;
  border-radius: 3px;
}
:global(.dbv) {
  font-size: 0.64rem;
  color: var(--mu);
  font-family: 'DM Mono', monospace;
  min-width: 38px;
  text-align: right;
}
:global(.pr) {
  display: flex;
  align-items: center;
  gap: 0.72rem;
  margin-bottom: 0.45rem;
}
:global(.pl) {
  font-size: 0.69rem;
  color: var(--t2);
  width: 150px;
  min-width: 150px;
  flex-shrink: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
:global(.pt) {
  flex: 1;
  height: 5px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
}
:global(.pf2) {
  height: 100%;
  border-radius: 3px;
}
:global(.pv) {
  font-size: 0.65rem;
  color: var(--mu);
  font-family: 'DM Mono', monospace;
  width: 110px;
  min-width: 110px;
  flex-shrink: 0;
  white-space: nowrap;
  text-align: right;
}
:global(.ch) {
  position: relative;
}
.nav {
  position: fixed;
  bottom: calc(3.5rem + env(safe-area-inset-bottom, 0px) + var(--study-vv-bottom-inset, 0px));
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  width: fit-content;
  max-width: calc(100vw - 1.5rem);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 0.58rem;
  /* Above .slide-id-ref and .mob-badges so arrows/dots receive clicks */
  z-index: 300;
  background: rgba(3, 11, 10, 0.95);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 3rem;
  padding: 0.44rem 0.82rem;
  /* iOS: fixed + transform on the same element breaks hit-testing; center with margins instead */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
.nb {
  width: 31px;
  height: 31px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.74rem;
  cursor: pointer;
  transition: all 0.2s;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
.nb:hover {
  background: rgba(17, 160, 165, 0.2);
  border-color: var(--gbr);
  color: var(--g);
}
.nb:disabled,
:global(.nb--disabled) {
  opacity: 0.3;
  cursor: not-allowed;
  pointer-events: none;
}
.nds {
  display: flex;
  gap: 5px;
  align-items: center;
  padding: 0 0.32rem;
}
:global(.nd) {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: all 0.2s;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
:global(.nd.active) {
  background: var(--g);
  width: 14px;
  border-radius: 3px;
}
.nc {
  font-family: 'DM Mono', monospace;
  font-size: 0.63rem;
  color: rgba(255, 255, 255, 0.32);
  min-width: 9ch;
  text-align: center;
  white-space: nowrap;
  flex-shrink: 0;
}
.slide-id-ref {
  position: fixed;
  bottom: calc(0.55rem + env(safe-area-inset-bottom, 0px) + var(--study-vv-bottom-inset, 0px));
  right: 1rem;
  /* Below .nav (300); corner copy control rarely overlaps the centered nav */
  z-index: 290;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  font-family: 'DM Mono', monospace;
  font-size: 0.58rem;
  letter-spacing: 0.04em;
  color: var(--mu);
  opacity: 0.38;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  text-align: right;
  display: inline-flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 0.35rem;
  max-width: calc(100vw - 1.5rem);
}
.slide-id-ref--show-feedback {
  opacity: 0.88;
}
.slide-id-ref__copied {
  font-size: 0.5rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--g);
}
.slide-id-ref:hover,
.slide-id-ref:focus-visible {
  opacity: 0.72;
}
.slide-id-ref:focus-visible {
  outline: 1px solid var(--gbr);
  outline-offset: 2px;
}
.study-presentation.light-theme .slide-id-ref {
  color: rgba(10, 18, 17, 0.42);
  opacity: 0.45;
}
.study-presentation.light-theme .slide-id-ref:hover,
.study-presentation.light-theme .slide-id-ref:focus-visible {
  opacity: 0.78;
}
.study-presentation.light-theme .slide-id-ref__copied {
  color: var(--g);
}
.study-presentation.light-theme .slide-id-ref--show-feedback {
  opacity: 0.92;
}
/* Below `StudyPresentation` Slides/Reader toggle (top-right, z-index 4000); stay above .nav (300). */
.dl-dropdown {
  position: fixed;
  top: calc(3.35rem + env(safe-area-inset-top, 0px));
  right: 1.6rem;
  z-index: 4100;
}
.dl-dropdown--open .dl-btn__chev {
  transform: rotate(180deg);
}
.dl-btn {
  display: flex;
  align-items: center;
  gap: 0.38rem;
  background: rgba(3, 11, 10, 0.85);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 2rem;
  padding: 0.38rem 0.68rem 0.38rem 0.68rem;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'DM Mono', monospace;
  font-size: 0.6rem;
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: all 0.2s;
}
.dl-btn__chev {
  flex-shrink: 0;
  opacity: 0.72;
  transition: transform 0.2s ease;
}
.dl-btn:hover {
  border-color: var(--gbr);
  color: var(--g);
}
.dl-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.dl-dropdown__menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 7.5rem;
  padding: 0.28rem;
  background: rgba(3, 11, 10, 0.94);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.55rem;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.dl-dropdown__item {
  margin: 0;
  padding: 0.45rem 0.65rem;
  border: none;
  border-radius: 0.38rem;
  background: transparent;
  color: rgba(255, 255, 255, 0.78);
  font-family: 'DM Mono', monospace;
  font-size: 0.62rem;
  letter-spacing: 0.05em;
  text-align: left;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}
.dl-dropdown__item:hover,
.dl-dropdown__item:focus-visible {
  background: rgba(17, 160, 165, 0.18);
  color: var(--g);
  outline: none;
}
.study-presentation.light-theme .dl-dropdown__menu {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
}
.study-presentation.light-theme .dl-dropdown__item {
  color: rgba(10, 18, 17, 0.78);
}
.study-presentation.light-theme .dl-dropdown__item:hover,
.study-presentation.light-theme .dl-dropdown__item:focus-visible {
  background: rgba(17, 160, 165, 0.14);
  color: var(--g);
}
:global(.btn) {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--g);
  color: #020a14;
  border-radius: 0.44rem;
  padding: 0.72rem 1.65rem;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  text-decoration: none;
  letter-spacing: 0.01em;
}
:global(.btn:hover) {
  background: var(--gb);
  transform: translateY(-2px);
  box-shadow: 0 10px 26px rgba(17, 160, 165, 0.42);
}
:global(.btng) {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: var(--g);
  border-radius: 0.44rem;
  padding: 0.72rem 1.45rem;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid var(--gbr);
  transition: all 0.2s;
  text-decoration: none;
}
:global(.btng:hover) {
  background: rgba(17, 160, 165, 0.14);
}
.mob-badges {
  position: fixed;
  bottom: calc(env(safe-area-inset-bottom, 0px) + var(--study-vv-bottom-inset, 0px));
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 0.4rem 0.75rem;
  z-index: 199;
  /* Decorative strip — was capturing clicks meant for slide CTAs (e.g. Restart) below it */
  pointer-events: none;
}
.study-citation-chip {
  position: fixed;
  bottom: calc(env(safe-area-inset-bottom, 0px) + var(--study-vv-bottom-inset, 0px));
  right: 0.75rem;
  z-index: 200;
  white-space: nowrap;
  pointer-events: auto;
}
@media (max-width: 640px) {
  .study-citation-chip {
    display: none;
  }
}

@media (max-width: 1024px) {
  .dl-dropdown {
    top: calc(3.1rem + env(safe-area-inset-top, 0px));
    right: 1.1rem;
  }
  /*
   * Tablet / narrow laptop: scroll the slide like phones so tall content isn’t trapped under the
   * fixed nav. (Desktop >1024 stays vertically centered with no scroll.)
   */
  :global(.slide) {
    padding: 3.85rem 1.35rem calc(8.25rem + var(--study-vv-bottom-inset, 0px));
    align-items: flex-start;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  :global(.slide::-webkit-scrollbar) {
    display: none;
  }
  :global(.slide.slide-extra-bottom) {
    padding-bottom: calc(12.5rem + var(--study-vv-bottom-inset, 0px));
  }
  :global(.sg4) {
    grid-template-columns: 1fr 1fr;
    gap: 0.72rem;
  }
  :global(.ttc) {
    grid-template-columns: 1fr 1fr;
    gap: 0.72rem;
  }
  :global(.bd) {
    font-size: 0.84rem;
    line-height: 1.64;
  }
  :global(.hb) {
    padding: 0.92rem 1rem;
  }
  :global(.hb p) {
    font-size: 0.78rem;
    line-height: 1.58;
  }
  :global(.sb .n) {
    font-size: 1.65rem;
  }
  :global(.sb .l) {
    font-size: 0.58rem;
  }
  .nav {
    bottom: calc(3rem + env(safe-area-inset-bottom, 0px) + var(--study-vv-bottom-inset, 0px));
  }
}
@media (max-width: 1024px) and (orientation: landscape) {
  :global(.slide) {
    padding-top: 3.4rem;
    padding-bottom: calc(8rem + var(--study-vv-bottom-inset, 0px));
  }
  :global(.slide.slide-extra-bottom) {
    padding-bottom: calc(12.5rem + var(--study-vv-bottom-inset, 0px));
  }
  :global(.ey) {
    margin-bottom: 0.42rem;
  }
  :global(.hl) {
    font-size: clamp(1.35rem, 2.25vw, 1.85rem);
  }
  :global(.bd) {
    font-size: 0.8rem;
    line-height: 1.55;
  }
  :global(.ca) {
    padding: 0.92rem 0.92rem;
  }
  :global(.ca p),
  :global(.tt) {
    font-size: 0.7rem;
    line-height: 1.45;
  }
  :global(.ch) {
    height: 145px !important;
  }
}
@media (max-width: 820px) and (orientation: portrait) {
  :global(.slide) {
    padding: 3.9rem 1.05rem calc(8rem + var(--study-vv-bottom-inset, 0px));
  }
  :global(.slide.slide-extra-bottom) {
    padding-bottom: calc(12.5rem + var(--study-vv-bottom-inset, 0px));
  }
  :global(.disp) {
    font-size: clamp(1.9rem, 6vw, 2.8rem);
  }
  :global(.hl) {
    font-size: clamp(1.28rem, 4vw, 1.8rem);
    line-height: 1.14;
  }
  :global(.sg4) {
    grid-template-columns: 1fr;
    gap: 0.62rem;
  }
  :global(.sb) {
    padding: 0.86rem 0.65rem;
  }
  :global(.sb .n) {
    font-size: 1.45rem;
  }
  :global(.sb .l) {
    font-size: 0.56rem;
  }
  .nav {
    bottom: calc(2.75rem + env(safe-area-inset-bottom, 0px) + var(--study-vv-bottom-inset, 0px));
  }
}
@media (max-width: 900px) {
  :global(.tc),
  :global(.tc.w) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.95rem;
  }
  :global(.ttc) {
    grid-template-columns: 1fr;
    gap: 0.65rem;
  }
}
@media (max-width: 640px) {
  :global(.slide) {
    padding: 4.25rem 1rem calc(8rem + var(--study-vv-bottom-inset, 0px));
    align-items: flex-start;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  :global(.slide.slide-extra-bottom) {
    padding-bottom: calc(12.5rem + var(--study-vv-bottom-inset, 0px));
  }
  :global(.slide::-webkit-scrollbar) {
    display: none;
  }
  :global(.ey) {
    margin-bottom: 0.5rem;
  }
  :global(.tc),
  :global(.tc.w) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.85rem;
  }
  :global(.ttc) {
    grid-template-columns: 1fr;
    gap: 0.65rem;
  }
  :global(.sg4) {
    grid-template-columns: 1fr 1fr;
    gap: 0.65rem;
  }
  :global(.tli) {
    padding: 0.55rem 0.65rem;
  }
  :global(.pl) {
    width: auto;
    min-width: 88px;
    flex-shrink: 1;
  }
  :global(.dbl) {
    min-width: 78px;
  }
  :global(.dbrs) {
    grid-template-columns: minmax(78px, max-content) minmax(0, 1fr) minmax(38px, max-content);
  }
  .nb {
    width: 40px;
    height: 40px;
  }
  .nav {
    bottom: calc(3.5rem + env(safe-area-inset-bottom, 0px) + var(--study-vv-bottom-inset, 0px));
  }
  .mob-badges {
    gap: 0.45rem;
    padding: 0.3rem 0.5rem;
  }
  /* Sit above the fixed badge strip so we don’t cover © / IMX line */
  .slide-id-ref {
    bottom: calc(2.25rem + env(safe-area-inset-bottom, 0px) + var(--study-vv-bottom-inset, 0px));
    right: 0.65rem;
    font-size: 0.52rem;
  }
  .mob-badges :global(.bg) {
    font-size: 0.52rem;
    padding: 0.22rem 0.45rem;
  }
}
@media (max-width: 430px) {
  .mob-badges :global(.bg) {
    font-size: 0.46rem;
    padding: 0.18rem 0.38rem;
  }
  /*
   * Must clear fixed .nav (bottom ~3.5rem + pill height ~3.4rem) + iOS vv inset.
   * Do not use a short bottom padding here — it overrides the 640px rule and covers content.
   */
  :global(.slide) {
    padding: 3.55rem 0.75rem calc(8rem + var(--study-vv-bottom-inset, 0px));
  }
  :global(.slide.slide-extra-bottom) {
    padding-bottom: calc(13rem + var(--study-vv-bottom-inset, 0px));
  }
  :global(.disp) {
    font-size: clamp(1.6rem, 7.5vw, 2.2rem);
  }
  :global(.hl) {
    font-size: clamp(1.1rem, 4.8vw, 1.4rem);
  }
  :global(.ey) {
    font-size: 0.55rem;
    margin-bottom: 0.35rem;
  }
  :global(.bd) {
    font-size: 0.8rem;
    line-height: 1.6;
  }
  :global(.ca) {
    padding: 0.8rem 0.85rem;
  }
  :global(.ca .vl) {
    font-size: 1.25rem;
  }
  :global(.sb) {
    padding: 0.8rem 0.55rem;
  }
  :global(.sb .n) {
    font-size: 1.55rem;
  }
  :global(.sb .l) {
    font-size: 0.56rem;
  }
  :global(.hb) {
    padding: 0.8rem 0.9rem;
  }
  :global(.hb p) {
    font-size: 0.75rem;
  }
  :global(.tli) {
    padding: 0.45rem 0.55rem;
  }
  :global(.ch) {
    height: 130px !important;
  }
}

/*
 * Cover + closing (`.slide--center`): keep the inner column centered in the slide frame. Other
 * slides use flex-start + scroll on short / narrow viewports so dense content clears fixed chrome.
 * Column flex + symmetric horizontal padding (see 1025–1580 rule below) so centering matches the
 * viewport on large / desktop widths, not a left-heavy padding box from the logo gutter.
 */
.study-presentation :global(.slide.slide--center) {
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
@media (min-width: 1025px) and (max-width: 1580px) {
  .study-presentation :global(.slide.slide--center) {
    padding-left: 2.75rem;
    padding-right: 2.75rem;
  }
}
@media (min-width: 1025px) and (max-height: 900px) {
  .study-presentation :global(.slide.slide--center) {
    align-items: center;
    justify-content: center;
    padding-top: calc(4.25rem + env(safe-area-inset-top, 0px));
    padding-right: 2.75rem;
    padding-left: 2.75rem;
    padding-bottom: calc(12.5rem + var(--study-vv-bottom-inset, 0px));
    overflow-y: auto;
  }
  .study-presentation :global(.slide.slide--center.slide-extra-bottom) {
    padding-bottom: calc(13rem + var(--study-vv-bottom-inset, 0px));
  }
}
@media (max-width: 1024px) {
  .study-presentation :global(.slide.slide--center) {
    align-items: center;
    justify-content: center;
  }
}
@media (max-width: 640px) {
  .study-presentation :global(.slide.slide--center) {
    align-items: center;
    justify-content: center;
  }
}

/*
 * Choropleth slide on phones AND tablets: full viewport width regardless of slide ID
 * (s4b for ADC/GLP-1, s12 for SGLT-2, etc.). The .study-s4b-choro-shell class is applied by
 * StudyChoroplethSlide unconditionally, so targeting it directly (without the slide-ID prefix)
 * makes this work for any choropleth slide.
 *
 * calc(50% - 50vw) is wrong when .inn is centered: use left:50% + margin-left:-50vw.
 * Strip .cc horizontal padding so the card isn't inset; pad title lines only.
 */
@media (max-width: 1024px) {
  :global(.study-presentation .slide:has(.study-s4b-choro-shell)) {
    overflow-x: hidden;
  }
  :global(.study-presentation .study-s4b-choro-shell) {
    position: relative;
    left: 50%;
    margin-left: -50vw;
    width: 100vw;
    max-width: 100vw;
    box-sizing: border-box;
  }
  :global(.study-presentation .study-s4b-choro-shell .choro-layout) {
    width: 100%;
    max-width: 100%;
  }
  :global(.study-presentation .study-s4b-choro-shell .choro-layout > div:first-child) {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
  :global(.study-presentation .study-s4b-choro-shell .choro-layout .cc) {
    padding-left: 0 !important;
    padding-right: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }
  :global(.study-presentation .study-s4b-choro-shell .choro-layout .ct),
  :global(.study-presentation .study-s4b-choro-shell .choro-layout .cs) {
    padding-left: 1rem;
    padding-right: 1rem;
    box-sizing: border-box;
  }
  :global(.study-presentation .study-s4b-choro-shell .choro-layout .cc > div:last-child) {
    padding-left: 1rem;
    padding-right: 1rem;
    box-sizing: border-box;
  }
  :global(.study-presentation .study-s4b-choro-shell .choro-side) {
    padding-left: 1rem;
    padding-right: 1rem;
    box-sizing: border-box;
    width: 100%;
  }
  :global(.study-presentation .study-s4b-choro-shell .choro-wrap) {
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
    border-radius: 0;
    max-height: none !important;
  }
}

/* ── Reader layout: scrollable stack, one wrapper div per slide ───────────── */
.study-presentation.study-reader-presentation .stage {
  display: block;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
}
.study-presentation.study-reader-presentation :global(.study-reader-slide-wrap) {
  display: block;
  width: 100%;
  box-sizing: border-box;
}
.study-presentation.study-reader-presentation :global(.slide) {
  position: relative !important;
  inset: auto !important;
  opacity: 1 !important;
  pointer-events: auto !important;
  transform: none !important;
  transition: none;
  min-height: min(100dvh, 100vh);
}
.study-presentation.study-reader-presentation :global(.slide.exit) {
  opacity: 1 !important;
  transform: none !important;
}
.study-presentation.study-reader-presentation :global(.slide .sg > *) {
  opacity: 1 !important;
  transform: none !important;
}
.study-presentation.study-reader-presentation :global(.slide .sg > *:nth-child(1)) {
  transition-delay: 0.06s;
}
.study-presentation.study-reader-presentation :global(.slide .sg > *:nth-child(2)) {
  transition-delay: 0.17s;
}
.study-presentation.study-reader-presentation :global(.slide .sg > *:nth-child(3)) {
  transition-delay: 0.29s;
}
.study-presentation.study-reader-presentation :global(.slide .sg > *:nth-child(4)) {
  transition-delay: 0.41s;
}
.study-presentation.study-reader-presentation :global(.slide .sg > *:nth-child(5)) {
  transition-delay: 0.53s;
}
.study-presentation.study-reader-presentation :global(.slide .sg > *:nth-child(6)) {
  transition-delay: 0.65s;
}
</style>
